/**
 * BlogPost — /blog/:slug per-article view.
 *
 * iter100 (P9): renders a single blog post statically from the
 * `blog/posts.js` registry. Emits Article schema.org JSON-LD with the
 * full set of Rich-Result-required fields (headline / datePublished /
 * author / publisher / image / mainEntityOfPage) so Google can serve
 * the article on SERPs with author/date snippets.
 *
 * 404 behaviour: unknown slugs render a `noindex` "not found" page that
 * keeps the layout consistent and offers a return link to the index.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import Navbar from '../../components/Navbar';
import FooterSection from '../../components/FooterSection';
import SEO from '../../components/SEO';
import { LanguageProvider } from '../../context/LanguageContext';
import { getPostBySlug } from './posts';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <LanguageProvider>
        <SEO
          title="Article not found"
          description="This NoFee blog post does not exist or has been moved."
          path={`/blog/${slug || ''}`}
          noindex
        />
        <div className="min-h-screen bg-black text-white antialiased" data-testid="blog-post-not-found">
          <Navbar forceSolid />
          <main className="pt-32 pb-24">
            <div className="max-w-[680px] mx-auto px-6 text-center">
              <BookOpen className="w-7 h-7 text-white/40 mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-white mb-2">Article not found</h1>
              <p className="text-sm text-white/55">
                The post you&rsquo;re looking for doesn&rsquo;t exist. Browse the rest of the blog below.
              </p>
              <Link
                to="/blog"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 text-black font-extrabold text-[13.5px]"
                data-testid="blog-post-back-to-index"
              >
                <ArrowLeft className="w-4 h-4" /> Back to the blog
              </Link>
            </div>
          </main>
          <FooterSection />
        </div>
      </LanguageProvider>
    );
  }

  const url = `https://nofee.restaurant/blog/${post.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    datePublished: post.datePublished,
    dateModified: post.dateModified || post.datePublished,
    author: { '@type': 'Organization', name: post.author || 'NoFee Team' },
    publisher: { '@id': 'https://nofee.restaurant/#org' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    image: post.cover ? [`https://nofee.restaurant${post.cover}`] : undefined,
  };

  return (
    <LanguageProvider>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={post.cover ? `https://nofee.restaurant${post.cover}` : undefined}
        type="article"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-black text-white antialiased" data-testid="blog-post">
        <Navbar forceSolid />

        <main className="pt-28 lg:pt-32 pb-20">
          <article className="max-w-[760px] mx-auto px-6 lg:px-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-gold-300 mb-6 transition-colors"
              data-testid="blog-post-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to the blog
            </Link>

            {post.tag && (
              <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-3">
                {post.tag}
              </p>
            )}
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.02em] text-white leading-[1.1]"
              data-testid="blog-post-title"
            >
              {post.title}
            </h1>
            <div className="mt-4 flex items-center gap-3 text-[12px] text-white/45">
              <span>{post.author || 'NoFee Team'}</span>
              <span aria-hidden="true">•</span>
              <time dateTime={post.datePublished}>
                {new Date(post.datePublished).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </time>
              {post.readTimeMin ? (
                <>
                  <span aria-hidden="true">•</span>
                  <span>{post.readTimeMin} min read</span>
                </>
              ) : null}
            </div>

            {post.cover && (
              <div className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden bg-zinc-900 border border-white/[0.06]">
                <img
                  src={post.cover}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Body — sanitised markdown-ish HTML. We control the content
                ourselves (no user-generated), so dangerouslySetInnerHTML is
                acceptable. Re-evaluate if we ever open this up to authors. */}
            <div
              className="mt-10 prose-blog text-white/80 leading-relaxed"
              data-testid="blog-post-body"
              dangerouslySetInnerHTML={{ __html: post.body }}
            />

            {/* CTA at the bottom */}
            <div className="mt-12 rounded-2xl border border-gold-500/25 bg-gradient-to-br from-zinc-900 to-black p-6 lg:p-8">
              <p className="text-[10.5px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-2">Ready to act on this?</p>
              <h3 className="text-xl font-black text-white leading-tight">See how much your restaurant could save with NoFee.</h3>
              <p className="mt-2 text-sm text-white/60">Plug in your numbers — get an instant comparison vs DoorDash and Uber Eats.</p>
              <Link
                to="/savings-calculator"
                className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[13.5px]"
                data-testid="blog-post-cta-calc"
              >
                Open the Savings Calculator <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </article>
        </main>

        <FooterSection />
      </div>

      {/* Minimal prose styling — we keep this in-component so it doesn't
          leak into the rest of the SaaS. Generic typography for blog
          body content rendered via dangerouslySetInnerHTML. */}
      <style>{`
        .prose-blog h2 { font-size: 1.5rem; font-weight: 800; color: white; margin-top: 2rem; margin-bottom: 0.75rem; letter-spacing: -0.01em; }
        .prose-blog h3 { font-size: 1.15rem; font-weight: 700; color: white; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .prose-blog p  { margin-bottom: 1.1rem; }
        .prose-blog ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 1.1rem; }
        .prose-blog ol { list-style: decimal; padding-left: 1.5rem; margin-bottom: 1.1rem; }
        .prose-blog li { margin-bottom: 0.4rem; }
        .prose-blog a  { color: #f5c542; text-decoration: underline; }
        .prose-blog strong { color: #fff; font-weight: 700; }
      `}</style>
    </LanguageProvider>
  );
};

export default BlogPost;
